import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useBackClose } from "../back.js";
import { useText } from "../i18n.js";
import { SIDEBAR } from "../text/sidebar.js";
import { MenuIcon, type MenuIconName } from "./MenuIcon.js";

export interface MenuItem {
  readonly label: string;
  readonly icon?: MenuIconName;
  readonly danger?: boolean;
  readonly checked?: boolean;
  readonly onSelect?: () => void;
  readonly children?: readonly MenuItem[];
}

export type Divider = "divider";

export interface MenuState {
  readonly x: number;
  readonly y: number;
  readonly items: ReadonlyArray<MenuItem | Divider>;
  /** A quiet line over the items, such as when a message was sent. */
  readonly heading?: string;
  /** The menu's name for a screen reader, when there is no list open. */
  readonly label?: string;
}

/** How far a menu keeps from the window's edges. */
const MENU_MARGIN = 8;

const BackIcon = (): React.JSX.Element => (
  <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
    <path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The prototype's row menu: opened with a right click or a long press, closed
 * by anything outside it. An item with a list of its own ("Move to") opens
 * that list in the same menu, in place of the items, with a way back at the
 * top left: a menu beside the menu would leave a phone's screen.
 */
export function ContextMenu({ menu, onClose }: { readonly menu: MenuState; readonly onClose: () => void }): React.JSX.Element {
  const t = useText(SIDEBAR);
  /** The item whose list is shown instead of the menu's own items; null for those. */
  const [page, setPage] = useState<number | null>(null);
  const element = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState({ left: menu.x, top: menu.y });
  /** The corner it grows from: the one nearest where it was opened, which moves when the window's edge pushes it over. */
  const origin = `${at.top < menu.y ? "bottom" : "top"} ${at.left < menu.x ? "right" : "left"}`;
  // Open is a page of its own to Back, on a phone: it closes the menu rather than the app, and a list inside it goes back to the menu.
  useBackClose(true, onClose);
  useBackClose(page != null, () => setPage(null));
  useEffect(() => {
    const key = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (page != null) setPage(null);
      else onClose();
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose, page]);
  // On screen near the pointer, measured as drawn: a list is taller or shorter than the menu it replaced.
  useLayoutEffect(() => {
    const box = element.current?.getBoundingClientRect();
    if (box == null) return;
    const left = Math.max(MENU_MARGIN, Math.min(menu.x, window.innerWidth - box.width - MENU_MARGIN));
    const top = Math.max(MENU_MARGIN, Math.min(menu.y, window.innerHeight - box.height - MENU_MARGIN));
    setAt((current) => (current.left === left && current.top === top ? current : { left, top }));
  }, [menu, page]);
  // The keyboard follows into a list and back out of it.
  const turned = useRef(false);
  useEffect(() => {
    if (!turned.current) {
      turned.current = true;
      return;
    }
    element.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [page]);
  /**
   * One highlight for the whole menu, gliding to the item under the pointer
   * or the keyboard rather than each item lighting on its own: it reads as
   * one thing moving, as a native menu's does. It fades away when the pointer
   * leaves, and starts over on a list.
   */
  const [glide, setGlide] = useState<{ top: number; height: number; danger: boolean; arriving: boolean } | null>(null);
  useEffect(() => setGlide(null), [page]);
  const follow = (target: EventTarget): void => {
    const item = (target as HTMLElement).closest<HTMLElement>(".context-item");
    if (item == null) return;
    // Coming back from nowhere it appears where it is wanted; only from one item to the next does it glide.
    setGlide((was) => ({ top: item.offsetTop, height: item.offsetHeight, danger: item.classList.contains("is-danger"), arriving: was == null || was.height === 0 }));
  };
  const glider = {
    onPointerOver: (event: React.PointerEvent) => follow(event.target),
    onFocus: (event: React.FocusEvent) => follow(event.target),
    onPointerLeave: () => setGlide((was) => (was == null ? null : { ...was, height: 0 })),
  };
  const glideBar = <div className={`context-glide${glide == null || glide.height === 0 ? "" : " shown"}${glide?.arriving === true ? " arriving" : ""}${glide?.danger === true ? " is-danger" : ""}`} style={glide == null ? undefined : { transform: `translateY(${glide.top}px)`, height: glide.height === 0 ? undefined : glide.height }} aria-hidden="true" />;
  const pick = (item: MenuItem): void => {
    if (item.children != null) return;
    onClose();
    item.onSelect?.();
  };
  const opened = page == null ? null : menu.items[page];
  const list = opened != null && opened !== "divider" && opened.children != null ? { label: opened.label, children: opened.children } : null;
  return (
    <>
      {/*
        A film over the page: a tap anywhere on it closes the menu and reaches
        nothing beneath. It closes on the click and not on the press, because a
        press that closed it would take the film away before the browser had
        finished the tap, and the click that followed would then be aimed at
        whatever the film had been covering — the row beneath, which would open
        its chat. Staying until the click lands is what makes the film a film.
      */}
      <div
        className="context-backdrop"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        onContextMenu={(event) => event.preventDefault()}
      />
      <div className="context-menu" role="menu" aria-label={list?.label ?? menu.label} ref={element} style={{ ...at, transformOrigin: origin }} onContextMenu={(event) => event.preventDefault()}>
        {list != null ? (
          <div className="context-page" key={`list-${page}`} {...glider}>
            {glideBar}
            <div className="context-head">
              <button type="button" className="context-back" onClick={() => setPage(null)} aria-label={t.backToMenu} title={t.back}>
                <BackIcon />
              </button>
              <span className="context-title">{list.label}</span>
            </div>
            <hr />
            {list.children.map((child, index) => (
              <button key={index} type="button" role="menuitem" className="context-item" onClick={() => pick(child)}>
                <span className="context-icon" aria-hidden="true">
                  {child.icon != null ? <MenuIcon name={child.icon} /> : null}
                </span>
                <span className="context-label">{child.label}</span>
                {child.checked === true ? <span className="context-check">✓</span> : null}
              </button>
            ))}
          </div>
        ) : (
          <div className="context-page" key="menu" {...glider}>
            {glideBar}
            {menu.heading != null ? <div className="context-heading">{menu.heading}</div> : null}
            {menu.items.map((item, index) =>
              item === "divider" ? (
                <hr key={index} />
              ) : (
                <button key={index} type="button" role="menuitem" className={`context-item${item.danger === true ? " is-danger" : ""}`} onClick={() => (item.children == null ? pick(item) : setPage(index))} aria-haspopup={item.children == null ? undefined : "menu"}>
                  <span className="context-icon" aria-hidden="true">
                    {item.icon != null ? <MenuIcon name={item.icon} /> : null}
                  </span>
                  <span className="context-label">{item.label}</span>
                  {item.children != null ? <span className="context-more">›</span> : item.checked === true ? <span className="context-check">✓</span> : null}
                </button>
              ),
            )}
          </div>
        )}
      </div>
    </>
  );
}
